import React, {useEffect, useLayoutEffect, useRef} from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Events from './components/Events';
import OldEvents from './components/OldEvents';
import EventPage from './components/EventPage';
import useCustomCursor from './components/useCustomCursor';
import Info from './components/Info';
import Projecte from './components/Projecte';
import GraffitiCanvas from './components/GraffitiCanvas';
import { wordpressApi } from './services/wordpress';
import { useDocumentMetadata } from './hooks/useDocumentMetadata';
import { buildCanonicalUrl } from './utils/eventUtils';
import {
  applyDocumentBackground,
  getMonthGradient,
  getMonthGraffitiColor,
} from './utils/backgroundUtils';
import { LocalizationProvider, useLocalization } from './localization';

const DEFAULT_BACKGROUND = getMonthGradient();
const DEFAULT_GRAFFITI_COLOR = getMonthGraffitiColor();

const applyBackground = (background: string) => {
  applyDocumentBackground(background);
};

const applyGraffitiColor = (color: string) => {
  document.documentElement.style.setProperty('--active-graffiti-color', color);
};

const getBackgroundFromEventDate = (eventDate?: string): string => {
  const month = Number(eventDate?.split('/')[1]);

  if (Number.isNaN(month) || month < 1 || month > 12) {
    return DEFAULT_BACKGROUND;
  }

  return getMonthGradient(month);
};

const getGraffitiColorFromEventDate = (eventDate?: string): string => {
  const month = Number(eventDate?.split('/')[1]);

  if (Number.isNaN(month) || month < 1 || month > 12) {
    return DEFAULT_GRAFFITI_COLOR;
  }

  return getMonthGraffitiColor(month);
};

const AppShell: React.FC = () => {
  const { copy, projectPagePath } = useLocalization();
  const location = useLocation();
  const navigate = useNavigate();
  const previousPathnameRef = useRef<string | null>(null);
  const homeBackgroundRef = useRef(DEFAULT_BACKGROUND);
  const homeGraffitiColorRef = useRef(DEFAULT_GRAFFITI_COLOR);

  useCustomCursor();

  useEffect(() => {
    document.body.classList.remove('click-cursor');

    return () => {
      document.body.classList.remove('click-cursor');
    };
  }, []);

  useEffect(() => {
    const isProjectRoute = ['/projecte', '/proyecto', '/project'].includes(location.pathname);

    if (isProjectRoute && location.pathname !== projectPagePath) {
      navigate({ pathname: projectPagePath, search: location.search }, { replace: true });
    }
  }, [location.pathname, location.search, navigate, projectPagePath]);

  useEffect(() => {
    if (!('scrollRestoration' in window.history)) {
      return;
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);

    document
      .querySelectorAll<HTMLElement>('.page-container, .events-container')
      .forEach((element) => {
        element.scrollTop = 0;
      });
  }, [location.pathname, location.search]);

  useLayoutEffect(() => {
    const previousPathname = previousPathnameRef.current;
    const isHomeRoute = location.pathname === '/';
    const cameFromHomeRoute = previousPathname === '/';

    if (isHomeRoute) {
      applyBackground(homeBackgroundRef.current);
      applyGraffitiColor(homeGraffitiColorRef.current);
      previousPathnameRef.current = location.pathname;
      return;
    }

    if (!isHomeRoute && !cameFromHomeRoute) {
      let isCancelled = false;

      const syncBackgroundWithHome = async () => {
        try {
          const response = await wordpressApi.getEvents('event_date', 'asc');
          const initialBackground = getBackgroundFromEventDate(
            response.data[0]?.acf?.event_date
          );
          const initialGraffitiColor = getGraffitiColorFromEventDate(
            response.data[0]?.acf?.event_date
          );

          homeBackgroundRef.current = initialBackground;
          homeGraffitiColorRef.current = initialGraffitiColor;

          if (!isCancelled) {
            applyBackground(initialBackground);
            applyGraffitiColor(initialGraffitiColor);
          }
        } catch (_error) {
          homeBackgroundRef.current = DEFAULT_BACKGROUND;
          homeGraffitiColorRef.current = DEFAULT_GRAFFITI_COLOR;

          if (!isCancelled) {
            applyBackground(DEFAULT_BACKGROUND);
            applyGraffitiColor(DEFAULT_GRAFFITI_COLOR);
          }
        }
      };

      void syncBackgroundWithHome();

      previousPathnameRef.current = location.pathname;
      return () => {
        isCancelled = true;
      };
    }

    previousPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const isEventRoute = location.pathname.startsWith('/events/');
  const defaultDescriptions: Record<string, string> = {
    '/': copy.metadata.homeDescription,
    '/past': copy.metadata.pastDescription,
    '/info': copy.metadata.infoDescription,
    '/projecte': copy.metadata.projectDescription,
    '/proyecto': copy.metadata.projectDescription,
    '/project': copy.metadata.projectDescription,
  };
  const defaultTitles: Record<string, string> = {
    '/': 'El Pumarejo',
    '/past': copy.metadata.pastTitle,
    '/info': copy.metadata.infoTitle,
    '/projecte': copy.metadata.projectTitle,
    '/proyecto': copy.metadata.projectTitle,
    '/project': copy.metadata.projectTitle,
  };

  useDocumentMetadata({
    title: isEventRoute
      ? document.title || 'El Pumarejo'
      : defaultTitles[location.pathname] || 'El Pumarejo',
    description: isEventRoute
      ? undefined
      : defaultDescriptions[location.pathname] || 'El Pumarejo.',
    canonicalUrl: buildCanonicalUrl(location.pathname),
  });

  return (
    <div className="app-shell d-flex flex-column min-vh-100">
      <div aria-hidden="true" className="app-background" />
      <GraffitiCanvas />
      <div>
        <img alt="" style={{display: 'none'}} src="/img/cursor.png" />
        <style>
          {`
            * {
              cursor: url('/img/cursor.png') 5 5, auto;
            }
            
            *.click-cursor * {
            cursor: url('/img/cursor-closed.png') 5 5, auto;
            }
          `}
        </style>
      </div>
      <Header />
      <main className="app-content flex-grow-1">
        <Routes>
          <Route path="/" element={<Events />} />
          <Route path="/events/:slug" element={<EventPage />} />
          <Route path="/past" element={<OldEvents />} />
          <Route path="/info" element={<Info />} />
          <Route path="/projecte" element={<Projecte />} />
          <Route path="/proyecto" element={<Projecte />} />
          <Route path="/project" element={<Projecte />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <LocalizationProvider>
        <AppShell />
      </LocalizationProvider>
    </Router>
  );
}

export default App;
